/**
 * ResetPasswordPage - conclusão do fluxo seguro de recuperação de acesso.
 */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { api, getApiErrorMessage } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { ArrowLeft, CheckCircle2, KeyRound } from "lucide-react";

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = useMemo(() => params.get("token") || "", [params]);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const submit = async (event) => {
        event.preventDefault();
        if (!token) {
            toast.error("O link de recuperação é inválido ou está incompleto.");
            return;
        }
        if (password !== confirmPassword) {
            toast.error("As palavras-passe não coincidem.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setSuccess(true);
            toast.success("Palavra-passe actualizada.");
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Não foi possível repor a palavra-passe."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center py-10 px-4 bg-hero-radial-light" data-testid="reset-password-page">
            <Card className="w-full max-w-md p-7 rounded-2xl bg-card border-border">
                <div className="flex justify-center mb-5">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B0B10] overflow-hidden">
                        <img src={LOGO_URL} alt="MeSafa" className="h-14 w-14 object-contain" />
                    </span>
                </div>
                {success ? (
                    <div className="text-center space-y-3">
                        <div className="mx-auto h-14 w-14 rounded-full bg-[#2DD4BF]/15 grid place-items-center">
                            <CheckCircle2 className="h-7 w-7 text-[#2DD4BF]" />
                        </div>
                        <h1 className="font-display text-xl font-semibold">Acesso recuperado</h1>
                        <p className="text-sm text-muted-foreground">A sua palavra-passe foi actualizada. Já pode iniciar sessão.</p>
                        <Button asChild className="w-full btn-grad-purple text-white h-11">
                            <Link to="/entrar">Entrar</Link>
                        </Button>
                    </div>
                ) : (
                    <>
                        <h1 className="font-display text-2xl font-semibold text-center">Definir nova palavra-passe</h1>
                        <p className="text-sm text-muted-foreground text-center mt-1">Utilize pelo menos 12 caracteres, com maiúscula, minúscula, número e símbolo.</p>
                        {!token && (
                            <p className="mt-4 text-sm text-destructive text-center">Este link não contém um token de recuperação válido.</p>
                        )}
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="reset-password">Nova palavra-passe</Label>
                                <Input id="reset-password" type="password" required minLength={12} maxLength={128} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" />
                            </div>
                            <div>
                                <Label htmlFor="reset-password-confirm">Confirmar palavra-passe</Label>
                                <Input id="reset-password-confirm" type="password" required minLength={12} maxLength={128} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" />
                            </div>
                            <Button type="submit" disabled={loading || !token || !password || !confirmPassword} className="w-full btn-grad-purple text-white h-11">
                                <KeyRound className="h-4 w-4 mr-2" />
                                {loading ? "A actualizar…" : "Actualizar palavra-passe"}
                            </Button>
                        </form>
                    </>
                )}
                <div className="mt-6 pt-4 border-t border-border text-center">
                    <Link to="/entrar" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                        <ArrowLeft className="h-3 w-3" /> Voltar ao login
                    </Link>
                </div>
            </Card>
        </div>
    );
}
